import { Object3D, PlaneGeometry, MeshBasicMaterial, Mesh, TextureLoader, SRGBColorSpace } from "three";
import * as htmlToImage from "html-to-image";

import { config } from "./config";
import { TO_RAD } from "~/lib/utils.js";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator?.userAgent);
const BATCH_SIZE = isMobile ? 2 : 4; // Process multiple items per batch for smoother loading
let processingQueue = [];
let isProcessing = false;
let geometryCache = {}; // Shared geometry cache
let geometryCacheRefCount = {}; // Reference count per geometry key

export default class MilestoneEvent extends Object3D {
    constructor(event, milestoneIndex, eventIndex) {
        super();
        this.event = event;
        const id = `slt-js-milestone-event-${milestoneIndex}-${eventIndex}`;

        const ele = document.getElementById(id);
        if (!ele) return;

        const eleW = ele.offsetWidth;
        const eleH = ele.offsetHeight;

        // Guard: skip if the element has no measurable size (e.g. parent is
        // display:none).  A 0×0 canvas is invalid for WebGL textures and
        // causes "texSubImage2D: no canvas" errors.
        if (eleW === 0 || eleH === 0) {
            console.warn(`[MilestoneEvent] Element #${id} has zero dimensions (${eleW}×${eleH}), skipping texture`);
            return;
        }

        this.eleW = eleW * config.pxToWorld;
        this.eleH = eleH * config.pxToWorld;

        // Use shared geometry with reference counting
        this.geometryKey = `${this.eleW.toFixed(2)}-${this.eleH.toFixed(2)}`;
        const geometry =
            geometryCache[this.geometryKey] ||
            (geometryCache[this.geometryKey] = new PlaneGeometry(
                this.eleW,
                this.eleH,
            ));
        geometryCacheRefCount[this.geometryKey] = (geometryCacheRefCount[this.geometryKey] || 0) + 1;
        // Create a simple placeholder immediately
        const tempMaterial = new MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.5,
        });

        const mesh = new Mesh(geometry, tempMaterial);
        mesh.rotation.y = config.camera.rotateY * TO_RAD;
        mesh.position.set(-10, -10, 0);
        this.add(mesh);

        // Track if this instance has been processed
        this.processed = false;

        // Add to processing queue instead of immediate processing
        processingQueue.push({
            element: ele,
            mesh: mesh,
            instance: this,
        });

        // Start processing if not already running
        if (!isProcessing) {
            MilestoneEvent.processNextBatch();
        }
    }
    // Static method to process queue in batches
    static processNextBatch() {
        if (processingQueue.length === 0) {
            isProcessing = false;
            return;
        }

        isProcessing = true;
        const batch = processingQueue.splice(0, BATCH_SIZE);

        // Process batch
        Promise.all(
            batch.map((item) => {
                const { element, mesh, instance } = item;
                // Skip if element is no longer in DOM
                if (!element || !document.body.contains(element))
                    return Promise.resolve();

                // Render at 2x for crisp text on HiDPI screens
                const dpr = Math.min(window.devicePixelRatio || 1, 2);
                return htmlToImage
                    .toBlob(element, { skipFonts: true, pixelRatio: dpr })
                    .then((blob) => {
                        if (!instance.parent) return; // Skip if removed from scene
                        // Create blob URL from the blob
                        const blobUrl = URL.createObjectURL(blob);
                        // Create texture loader
                        const loader = new TextureLoader();
                        // Load the blob URL as a texture
                        loader.load(
                            blobUrl,
                            (texture) => {
                                // Set texture properties
                                texture.colorSpace = SRGBColorSpace;
                                texture.generateMipmaps = false;

                                // Update material
                                mesh.material.dispose();
                                mesh.material = new MeshBasicMaterial({
                                    map: texture,
                                    transparent: true,
                                    depthWrite: false,
                                });

                                // Clean up blob URL
                                URL.revokeObjectURL(blobUrl);

                                instance.processed = true;
                            },
                            undefined, // onProgress
                            () => {
                                // onError — revoke blob URL to prevent memory leak
                                URL.revokeObjectURL(blobUrl);
                            },
                        );
                    })
                    .catch(() => {
                        // html-to-image capture failed — nothing to clean up
                    });
            }),
        ).then(() => {
            // Schedule next batch with delay
            setTimeout(
                () => {
                    MilestoneEvent.processNextBatch();
                },
                isMobile ? 300 : 100,
            ); // Longer delay on mobile
        });
    }
    destroy() {
        // Release geometry cache reference
        if (this.geometryKey && geometryCacheRefCount[this.geometryKey]) {
            geometryCacheRefCount[this.geometryKey]--;
            if (geometryCacheRefCount[this.geometryKey] <= 0) {
                // Last user — dispose and remove from cache
                if (geometryCache[this.geometryKey]) {
                    geometryCache[this.geometryKey].dispose();
                    delete geometryCache[this.geometryKey];
                }
                delete geometryCacheRefCount[this.geometryKey];
            }
        }

        // Dispose of all child meshes and their resources
        this.traverse((object) => {
            // Skip shared geometry disposal — handled by ref count above
            if (object.material) {
                if (object.material.map) {
                    object.material.map.dispose();
                }
                object.material.dispose();
            }
        });

        // Clear all children
        while (this.children.length > 0) {
            this.remove(this.children[0]);
        }
    }
}
